// src/components/popups/ViewersDrawer.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './ViewersDrawer.css';
import placeholderProfile from '../../assets/placeholder-profile.png';
import LikeDrawerSkeleton from '../skeleton/LikeDrawerSkeleton';
import api from '../../api';

/**
 * ViewersDrawer
 * - Owner-only bottom drawer listing who viewed a story.
 * - Mirrors LikeDrawer UI: avatar + username only.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   storyId?: string        // If provided, will fetch viewers via API
 *   initialViewers?: Array  // Optional preloaded viewers (won't re-fetch)
 */
export default function ViewersDrawer({ open, onClose, storyId, initialViewers }) {
  const [loading, setLoading] = useState(true);
  const [viewers, setViewers] = useState(initialViewers || []);
  const [error, setError] = useState('');

  // Avoid duplicate fetches for the same story while the drawer is open
  const requestedKeyRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    // If storyId changed while open, reset local list unless initialViewers provided
    if (storyId && requestedKeyRef.current !== storyId) {
      requestedKeyRef.current = null;
      setViewers(initialViewers || []);
    }

    // If we already have initial data, just show it
    if (Array.isArray(initialViewers) && initialViewers.length > 0) {
      setLoading(false);
      setError('');
      return;
    }

    if (!storyId) {
      setLoading(false);
      setError('Missing story id.');
      return;
    }

    // Prevent re-fetching repeatedly while open
    if (requestedKeyRef.current === storyId) return;
    requestedKeyRef.current = storyId;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get(`/stories/${storyId}/viewers`);
        if (cancelled) return;

        // Backend returns { viewers: [...], count }
        const arr = Array.isArray(res.data)
          ? res.data
          : (res.data?.viewers || []);

        setViewers(arr);
      } catch (e) {
        if (!cancelled) {
          const msg =
            e?.response?.data?.error ||
            e?.response?.data?.message ||
            e?.message ||
            'Failed to load viewers.';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // Only depend on `open` and `storyId`—NOT on array identities like initialViewers
  }, [open, storyId, initialViewers]);

  if (!open) return null;

  return (
    <div className="viewers-backdrop" onClick={onClose}>
      <div className="viewers-content" onClick={(e) => e.stopPropagation()}>
        <div className="viewers-header">
          <span>Views</span>
          <button className="viewers-close" onClick={onClose}>✕</button>
        </div>

        <div className="viewers-scroll">
          {loading ? (
            <LikeDrawerSkeleton />
          ) : error ? (
            <p className="viewers-empty">{error}</p>
          ) : viewers.length === 0 ? (
            <p className="viewers-empty">No views yet.</p>
          ) : (
            viewers.map((u, i) => {
              const avatar =
                (typeof u.profilePic === 'string' && u.profilePic.startsWith('http')) ? u.profilePic :
                (typeof u.avatarUrl === 'string' && u.avatarUrl.startsWith('http')) ? u.avatarUrl :
                placeholderProfile;

              const uname = u.username || u.handle || 'unknown';

              return (
                <div key={u._id || u.userId || uname || i} className="viewers-entry">
                  <img
                    src={avatar}
                    alt="avatar"
                    className="viewers-avatar"
                    onError={(e) => { e.currentTarget.src = placeholderProfile; }}
                  />
                  <Link to={`/user/${uname}`} className="viewers-username">
                    @{uname}
                  </Link>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
